import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import { cn } from "../../lib/utils";
import { ChevronDown, Check } from "lucide-react";

// Create context to share state between components
const SelectContext = createContext({});

const Select = ({ children, value, onValueChange, defaultValue, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
  const [selectedLabel, setSelectedLabel] = useState('');
  const selectRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleValueChange = (newValue, label) => {
    setSelectedValue(newValue);
    setSelectedLabel(label);
    onValueChange?.(newValue);
    setIsOpen(false);
  };

  return (
    <SelectContext.Provider value={{ 
      selectedValue, 
      selectedLabel,
      onValueChange: handleValueChange, 
      isOpen, 
      setIsOpen,
      setSelectedLabel
    }}>
      <div className="relative" ref={selectRef} {...props}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

const SelectTrigger = React.forwardRef(({ 
  className, 
  children, 
  ...props 
}, ref) => {
  const { isOpen, setIsOpen } = useContext(SelectContext);
  
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
      }}
      {...props}
    >
      {children}
      <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform duration-200", isOpen && "rotate-180")} />
    </button>
  );
});
SelectTrigger.displayName = "SelectTrigger";

const SelectValue = ({ placeholder }) => {
  const { selectedValue, selectedLabel } = useContext(SelectContext);
  const displayText = selectedLabel || selectedValue || placeholder;
  
  return (
    <span className={cn(
      "text-left block truncate",
      !selectedValue && "text-muted-foreground"
    )}>
      {displayText}
    </span>
  );
};

const SelectContent = ({ children, className, ...props }) => {
  const { isOpen } = useContext(SelectContext);
  
  if (!isOpen) return null;
  
  return (
    <div
      className={cn(
        "absolute top-full left-0 z-[100] w-full overflow-hidden rounded-md border bg-white text-gray-900 shadow-lg animate-in fade-in-0 zoom-in-95",
        "mt-1 max-h-60 overflow-y-auto",
        className
      )}
      {...props}
    >
      <div className="p-1">
        {children}
      </div>
    </div>
  );
};

const SelectItem = ({ className, children, value, ...props }) => {
  const { selectedValue, onValueChange } = useContext(SelectContext);
  const isSelected = selectedValue === value;
  
  return (
    <div
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none",
        "hover:bg-sky-50 hover:text-sky-600",
        isSelected && "bg-sky-50 text-sky-600",
        className
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onValueChange?.(value, children);
      }}
      {...props}
    >
      <span className="flex-1">{children}</span>
      {isSelected && <Check className="h-4 w-4 ml-2" />}
    </div>
  );
};

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue };